import type { FC } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import type { EnergyPackage } from '@lunit/shared-types';

interface EnergyPackageCardProps {
  package: EnergyPackage;
  onPurchase: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const formatCoins = (value: number) => value.toLocaleString();

export const EnergyPackageCard: FC<EnergyPackageCardProps> = ({
  package: pkg,
  onPurchase,
  disabled = false,
  loading = false
}) => {
  const totalEnergy = pkg.energy + Math.floor(pkg.energy * (pkg.bonus / 100));
  const hasBonus = pkg.bonus > 0;

  return (
    <div
      className={`relative rounded-xl border-2 p-4 transition-all ${
        pkg.popular
          ? 'border-primary bg-gradient-to-br from-primary/10 to-success/10'
          : 'border-white/10 bg-white/5'
      } ${disabled ? 'opacity-50' : 'hover:border-primary/60 hover:shadow-neon'}`}
    >
      {pkg.popular && (
        <div className="absolute -top-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-gradient-neon px-3 py-1 text-xs font-bold text-black shadow-neon">
          <Sparkles className="h-3 w-3" />
          Popular
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="text-2xl font-bold">
              {pkg.energy}
              {hasBonus && (
                <span className="ml-1 text-lg text-green-400">
                  +{Math.floor(pkg.energy * (pkg.bonus / 100))}
                </span>
              )}
            </div>
            <div className="text-xs uppercase tracking-wide text-white/60">Energy</div>
          </div>
        </div>

        {hasBonus && (
          <div className="rounded border border-green-500 bg-green-500/20 px-2 py-1 text-xs font-semibold text-green-300">
            +{pkg.bonus}% bonus
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-lg font-bold text-primary">
          🪙 {formatCoins(pkg.price)}
        </div>

        <button
          type="button"
          onClick={onPurchase}
          disabled={disabled || loading}
          className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
            disabled
              ? 'cursor-not-allowed bg-gray-500 text-white/70'
              : 'bg-gradient-electric text-white hover:shadow-glow'
          }`}
        >
          {loading ? (
            <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            'Buy'
          )}
        </button>
      </div>

      {hasBonus && (
        <div className="mt-3 text-center text-xs text-white/60">
          Total: {totalEnergy} energy for {pkg.price.toLocaleString()} coins
        </div>
      )}
    </div>
  );
};

